// src/pages/RelatorioICV.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import GraficoPerfilICV from './GraficoPerfilICV';
import interpretacaoICV from '../utils/interpretacaoICV';
import { NOMES_FATORES } from '../utils/tabelaConversaoICV';

export default function RelatorioICV() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const state = useMemo(() => location.state || {}, [location.state]);
  const sessionFromUrl = searchParams.get('session');
  const sessionId = sessionFromUrl || state.sessionId || null;
  const acesso = searchParams.get('acesso');

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setCarregando(false);
      setErro('Sessão da avaliação não encontrada.');
      return;
    }

    if (!sessionFromUrl && state.sessionId) {
      navigate(`/relatorio-icv?session=${encodeURIComponent(state.sessionId)}`, {
        replace: true,
        state
      });
      return;
    }

    let ativo = true;

    async function carregarResultado() {
      setCarregando(true);
      setErro('');

      try {
        const response = await fetch(
          `${acesso === 'admin' ? '/api/admin' : acesso === 'coach' ? '/api/coach' : acesso === 'participante' ? '/api/participante' : '/api'}/sessions/${encodeURIComponent(sessionId)}/result`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'include'
          }
        );

        let data;
        try {
          data = await response.json();
        } catch {
          throw new Error(`Resposta inválida do servidor (HTTP ${response.status}).`);
        }

        if (!response.ok || data?.ok === false) {
          throw new Error(data?.error || `Erro HTTP ${response.status}.`);
        }

        if (!data?.result) {
          throw new Error('O resultado desta avaliação ainda não está disponível.');
        }

        if (ativo) setDados(data);
      } catch (e) {
        if (ativo) setErro(e.message || 'Não foi possível carregar o relatório.');
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregarResultado();

    return () => {
      ativo = false;
    };
  }, [sessionId, sessionFromUrl, state, navigate, acesso]);

  const ordem = useMemo(
    () => ['DGV','R','A','I','II','III','IV','V','VI','VII','VIII','IX'],
    []
  );

  const ptResultados = useMemo(() => dados?.result?.pts || {}, [dados]);
  const brutos = useMemo(() => dados?.result?.brutos || {}, [dados]);
  const participante = dados?.participant || {};
  const sessao = dados?.session || {};

  const blocos = useMemo(() => {
    return ordem.map((fator) => {
      const pt = Number(ptResultados?.[fator] ?? 0);
      const bruto = Number(brutos?.[fator] ?? 0);

      const faixaKey =
        pt <= 1 ? 'muito_baixo' :
        pt <= 4 ? 'baixo' :
        pt <= 6 ? 'medio' :
        pt <= 8 ? 'acima_media' : 'alto';

      const faixaRotulo =
        faixaKey === 'muito_baixo' ? 'muito baixo' :
        faixaKey === 'baixo' ? 'baixo' :
        faixaKey === 'medio' ? 'médio' :
        faixaKey === 'acima_media' ? 'acima da média' : 'alto';

      const texto = (interpretacaoICV[fator] || {})[faixaKey] || '';

      return {
        fator,
        nome: NOMES_FATORES[fator] || fator,
        pt,
        bruto,
        faixaRotulo,
        texto
      };
    });
  }, [ordem, ptResultados, brutos]);

  function formatarData(data) {
    if (!data) return '—';

    try {
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(data));
    } catch {
      return String(data);
    }
  }

  if (carregando) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <h2>Carregando relatório...</h2>
      </div>
    );
  }

  if (erro || !dados) {
    return (
      <div style={{ padding: 32 }}>
        <h2>Relatório não encontrado</h2>
        <p style={{ color: '#666', marginTop: 8 }}>
          {erro || 'Não foi possível localizar esta avaliação.'}
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: 16,
            padding: '10px 18px',
            borderRadius: 7,
            border: 'none',
            background: '#242F3C',
            color: '#fff',
            fontWeight: 700,
            cursor: 'pointer'
          }}
        >
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 920,
        margin: '2rem auto',
        background: '#fff',
        borderRadius: 12,
        padding: 32,
        boxShadow: '0 2px 16px rgba(36,47,60,0.08)'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap'
        }}
      >
        <h2 style={{ fontWeight: 800, fontSize: 28, margin: 0 }}>
          Relatório Técnico do ICV
        </h2>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="no-print"
            style={{
              padding: '10px 18px',
              borderRadius: 7,
              border: '1px solid #e3e7ee',
              background: '#f7f9fc',
              cursor: 'pointer'
            }}
            onClick={() => navigate(-1)}
          >
            Voltar
          </button>

          <button
            className="no-print"
            style={{
              padding: '10px 18px',
              borderRadius: 7,
              border: 'none',
              background: '#242F3C',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer'
            }}
            onClick={() => window.print()}
          >
            Imprimir
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          padding: 18,
          border: '1px solid #E6EDF3',
          borderRadius: 10,
          background: '#F8FAFC'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              AVALIADO
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#242F3C', marginTop: 3 }}>
              {participante.nome || '—'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              E-MAIL
            </div>
            <div style={{ fontSize: 15, color: '#242F3C', marginTop: 3 }}>
              {participante.email || '—'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              EMPRESA
            </div>
            <div style={{ fontSize: 15, color: '#242F3C', marginTop: 3 }}>
              {participante.empresa || '—'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              CARGO
            </div>
            <div style={{ fontSize: 15, color: '#242F3C', marginTop: 3 }}>
              {participante.cargo || '—'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              DATA DA AVALIAÇÃO
            </div>
            <div style={{ fontSize: 15, color: '#242F3C', marginTop: 3 }}>
              {formatarData(sessao.scored_at || sessao.submitted_at || sessao.started_at)}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#667085', fontWeight: 700 }}>
              SESSÃO
            </div>
            <div
              style={{
                fontSize: 12,
                color: '#667085',
                marginTop: 3,
                wordBreak: 'break-all'
              }}
            >
              {sessao.id || sessionId}
            </div>
          </div>
        </div>
      </div>

      <div style={{ margin: '24px 0' }}>
        <GraficoPerfilICV
          ptResultados={Object.fromEntries(
            ordem.map(k => [k, Number(ptResultados[k] ?? 0)])
          )}
        />
      </div>

      <div style={{ color: '#333', lineHeight: 1.7, marginTop: 8 }}>
        {blocos.map(({ fator, nome, pt, bruto, faixaRotulo, texto }) => (
          <div
            key={fator}
            style={{
              marginBottom: 22,
              paddingBottom: 18,
              borderBottom: '1px solid #eee'
            }}
          >
            <strong style={{ fontSize: 19 }}>
              {fator} — {nome}
              <span
                style={{
                  color: '#888',
                  fontSize: 14,
                  fontWeight: 400
                }}
              >
                {' '}({faixaRotulo}, PT = {pt}, bruto = {bruto})
              </span>
            </strong>

            <div style={{ marginTop: 7 }}>
              {texto}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


